import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

import type { Contract, Target } from "../types";

// Input/output for the cross-border.customs.line-items contract. The artifact
// returns the line items that should run for customs (drop, transform, or inject
// synthetic items). Runs at checkout and fulfillment over the same shape.

const moneySchema = z.object({
  amount: z.number().finite().nonnegative(),
  currency: createEnum(Currency, { enumName: "Currency" }),
});

const weightSchema = z.object({ grams: z.number().nonnegative() });

const customsLineItemSchema = z.object({
  // Stable id: variant id for real items, any unique string for synthetic ones.
  // Synthetic items are identified by `variantId === null`.
  key: z.string(),
  variantId: z.string().nullable(),
  productId: z.string().nullable(),
  sku: z.string().nullable(),
  // Optional so existing artifacts returning synthetic items still validate.
  barcode: z.string().nullish(),
  description: z.string(),
  quantity: z.number().int().nonnegative(),
  unitValue: moneySchema,
  weight: weightSchema,
  hsCode: z.string().nullable(),
  countryOfOrigin: z.string().nullable(),
  properties: z.record(z.string(), z.string()),
  // Configured metafields, kept split by level so a product and a variant
  // sharing a `namespace.key` don't collide (the variant would otherwise mask
  // the product value).
  productMetafields: z.record(z.string(), z.string().nullable()),
  variantMetafields: z.record(z.string(), z.string().nullable()),
});
export type CustomsLineItem = z.infer<typeof customsLineItemSchema>;

const destinationSchema = z.object({
  country: z.string(),
  province: z.string().optional(),
  postalCode: z.string().optional(),
});

const originSchema = z.object({
  country: z.string(),
  province: z.string().optional(),
});

export const customsInputSchema = z.object({
  items: z.array(customsLineItemSchema),
  destination: destinationSchema,
  origin: originSchema,
  currency: createEnum(Currency, { enumName: "Currency" }),
});

// Returned items need an hsCode (or a classifiable description) — carriers
// reject customs items without a harmonization code.
export const customsOutputSchema = z.array(customsLineItemSchema);
export type CustomsOutput = z.infer<typeof customsOutputSchema>;

export const customsTarget = {
  id: "cross-border.customs.line-items",
  displayName: "Customs line items",
  description:
    "Transforms a cart's line items into the line items that should run for " +
    "customs. May drop rows (e.g. $0.00 add-ons), adjust declared value / HS " +
    "code / country of origin, or inject synthetic items not present in Shopify. " +
    "Runs both at checkout (duty quote) and at fulfillment (commercial invoice).",
} as const satisfies Target;

// Ambient `Money` + `money` helper for the artifact's type env; runtime impl is
// in customsPreludeJs. No `convert` — customs has no exchange-rate context.
const currencyUnion = Object.values(Currency)
  .map((c) => `"${c}"`)
  .join(" | ");

const customsPreludeDts = `\
declare type Currency = ${currencyUnion};
declare type Money = { amount: number; currency: Currency };

// Same-currency arithmetic; ops on mismatched currencies throw.
declare const money: {
  add(a: Money, b: Money): Money;
  subtract(a: Money, b: Money): Money;
  multiply(m: Money, n: number): Money;
  sum(ms: Money[]): Money;
  max(a: Money, b: Money): Money;
  min(a: Money, b: Money): Money;
  zero(currency: Currency): Money;
};
`;

const customsPreludeJs = `\
const money = (() => {
  const requireSameCurrency = (a, b) => {
    if (a.currency !== b.currency) {
      throw new Error(
        \`Currency mismatch: \${a.currency} vs \${b.currency}.\`,
      );
    }
  };
  return {
    add: (a, b) => {
      requireSameCurrency(a, b);
      return { amount: a.amount + b.amount, currency: a.currency };
    },
    subtract: (a, b) => {
      requireSameCurrency(a, b);
      return { amount: a.amount - b.amount, currency: a.currency };
    },
    multiply: (m, n) => ({ amount: m.amount * n, currency: m.currency }),
    sum: (ms) => {
      if (!ms || ms.length === 0) {
        throw new Error("money.sum requires a non-empty array; use money.zero() for an empty case.");
      }
      let acc = { amount: 0, currency: ms[0].currency };
      for (const m of ms) {
        requireSameCurrency(acc, m);
        acc = { amount: acc.amount + m.amount, currency: acc.currency };
      }
      return acc;
    },
    max: (a, b) => {
      requireSameCurrency(a, b);
      return a.amount >= b.amount ? a : b;
    },
    min: (a, b) => {
      requireSameCurrency(a, b);
      return a.amount <= b.amount ? a : b;
    },
    zero: (currency) => ({ amount: 0, currency }),
  };
})();
`;

export const customsStarterCode = `\
// Edit this function. It receives the cart's line items (with HS code, country
// of origin, line-item properties, and configured metafields) and returns the
// line items that should run for customs.
//
// You can drop rows, transform them, or inject synthetic items that don't exist
// in Shopify (set variantId / productId to null and give them a unique \`key\`).
// Every returned item should carry an \`hsCode\` — carriers reject customs items
// without one. The \`money\` helper namespace is available as a global.
export default (input: Input): Output => {
  // Drop zero-value add-on rows that ride the cart as line-item properties
  // (e.g. a "Build Time" selection); they aren't physical goods and shouldn't
  // be declared at customs.
  return input.items.filter((item) => item.unitValue.amount > 0);
};
`;

export const customsContractV1: Contract = {
  id: "cross-border.customs.line-items@2026-06.1",
  targetId: customsTarget.id,
  inputSchema: customsInputSchema,
  outputSchema: customsOutputSchema,
  tools: [],
  runtimePreludeJs: customsPreludeJs,
  runtimePreludeDts: customsPreludeDts,
  sampleInput: {
    items: [
      {
        key: "var_1",
        variantId: "var_1",
        productId: "prod_1",
        sku: "SKU-001",
        description: "Wireless Gaming Controller",
        quantity: 1,
        unitValue: { amount: 199.99, currency: Currency.USD },
        weight: { grams: 600 },
        hsCode: "950450",
        countryOfOrigin: "US",
        properties: { _addon_group: "addon-group-1" },
        productMetafields: { "custom.material": "ABS plastic" },
        variantMetafields: { "custom.country_of_origin": "US" },
      },
      {
        key: "var_2",
        variantId: "var_2",
        productId: "prod_2",
        sku: "SKU-BUILD",
        description: "Standard Build Time (4-6 Weeks est)",
        quantity: 1,
        unitValue: { amount: 0, currency: Currency.USD },
        weight: { grams: 0 },
        hsCode: null,
        countryOfOrigin: null,
        properties: { _addon_group: "addon-group-1" },
        productMetafields: {},
        variantMetafields: {},
      },
    ],
    destination: { country: "CA", province: "ON", postalCode: "M5V 2T6" },
    origin: { country: "US", province: "UT" },
    currency: Currency.USD,
  } satisfies z.infer<typeof customsInputSchema>,
};
